/**
 * TanStack Query хуки уведомлений.
 *
 * Mock-режим: локальные jotai-атомы (`notificationsAtom`, `unreadCountAtom`)
 * из `@lib/atoms` для UI-демо. Реальный режим: workspace-scoped эндпоинты
 * `/api/user/colver/v1/notifications`.
 *
 * Поллинг: бейдж — 30 сек (refetchInterval); лента — по запросу (enabled).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {useAtom, useAtomValue} from 'jotai';
import {useEffect, useMemo} from 'react';
import {useAuth} from '@api/auth';
import {MOCK_TODAY} from '@lib/utils';
import {getMockNotification, getMockSettings, updateMockSettings} from './mock';
import {useActiveWorkspace} from '@api/workspaces';
import {notificationsAtom, unreadCountAtom} from '@lib/atoms';
import {IS_MOCK} from '../mock-mode';
import {notificationsClient} from './client';
import {readAllNotifications} from './bulk-read';
import {adaptNotification, type NotificationDisplay} from './types';
import type {ChannelName, NotificationsListRequest} from './types';

const COUNT_REFRESH_MS = 30_000;

/** Ключи кэша уведомлений. */
export const notificationKeys = {
  all: ['notifications'] as const,
  list: (ws: string, actor: string | null, params?: NotificationsListRequest) =>
    ['notifications', 'list', actor, ws, params] as const,
  count: (ws: string, actor: string | null) =>
    ['notifications', 'count', actor, ws] as const,
  detail: (sid: string | null, actor: string | null) =>
    ['notifications', 'detail', actor, sid] as const,
};

/** Смена пользователя удаляет персональный кэш прежней сессии. */
const useNotificationActor = () => {
  const {currentUser} = useAuth();
  const qc = useQueryClient();
  const actor = currentUser?.sid ?? (IS_MOCK ? 'mock-user' : null);
  useEffect(() => {
    qc.removeQueries({
      predicate: (q) =>
        q.queryKey[0] === 'notifications' && q.queryKey[2] !== actor,
    });
  }, [actor, qc]);
  return actor;
};

/** Guarded detail запрашивается заново при переходе и возвращении к окну. */
export const useNotification = (sid: string | null) => {
  const actor = useNotificationActor();
  return useQuery({
    queryKey: notificationKeys.detail(sid, actor),
    queryFn: () =>
      IS_MOCK
        ? getMockNotification(sid as string)
        : notificationsClient.get(sid as string),
    enabled: Boolean(sid) && Boolean(actor),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });
};

/** Адаптация массива моков в NotificationDisplay (для mock-режима). */
const adaptMocks = (
  items: import('@lib/atoms').NotificationItem[],
): NotificationDisplay[] =>
  items.map((m) => ({
    id: m.id,
    kind:
      m.type === 'task'
        ? 'task'
        : m.type === 'report'
          ? 'analytics-report'
          : m.type === 'digest'
            ? 'digest'
            : 'instant',
    priority: m.type === 'priority-1' ? 1 : m.type === 'alert' ? 2 : null,
    unread: m.unread,
    title: m.title,
    description: m.desc,
    reportCounts: m.reportCounts,
    occurredAt: `${MOCK_TODAY}T11:55:00Z`,
    callId: m.callId ? String(m.callId) : undefined,
    taskId: m.taskId,
    sourcePath: m.taskId
      ? `/tasks?taskId=${encodeURIComponent(m.taskId)}`
      : m.id === 'NF-weekly'
        ? '/dashboard?notificationSid=NF-weekly&queue=management'
        : undefined,
  }));

// ─── Счётчик непрочитанных (бейдж) ─────────────────────────────────────────

/** Счётчик непрочитанных: API polling 30с / derived atom в mock. */
export const useNotificationsCount = () => {
  const {activeId} = useActiveWorkspace();
  const actor = useNotificationActor();
  const mockCount = useAtomValue(unreadCountAtom);

  const query = useQuery({
    queryKey: notificationKeys.count(activeId ?? 'mock', actor),
    queryFn: async () => {
      if (IS_MOCK) return mockCount;
      const res = await notificationsClient.count(activeId as string);
      return res.unreadCount;
    },
    enabled: IS_MOCK || (Boolean(activeId) && Boolean(actor)),
    refetchInterval: IS_MOCK ? false : COUNT_REFRESH_MS,
    initialData: IS_MOCK ? mockCount : undefined,
  });
  return {...query, data: IS_MOCK ? mockCount : query.data};
};

// ─── Лента уведомлений (infinite scroll) ────────────────────────────────────

interface UseNotificationsOptions {
  /** Лимит на страницу (дефолт 20). */
  limit?: number;
  /** Включить запрос (только при открытом дровере). */
  enabled?: boolean;
}

/** Лента уведомлений с infinite scroll (курсор: `before` = occurredAt). */
export const useNotifications = ({
  limit = 20,
  enabled = true,
}: UseNotificationsOptions = {}) => {
  const {activeId} = useActiveWorkspace();
  const actor = useNotificationActor();
  const [mockItems] = useAtom(notificationsAtom);

  // Mock: flat list из атома.
  const mockDisplay = useMemo(() => adaptMocks(mockItems), [mockItems]);

  const infinite = useInfiniteQuery({
    queryKey: notificationKeys.list(activeId ?? 'mock', actor, {limit}),
    queryFn: async ({pageParam}: {pageParam?: string}) => {
      if (IS_MOCK) return mockDisplay;
      const body: NotificationsListRequest = {limit};
      if (pageParam) body.before = pageParam;
      const raw = await notificationsClient.list(activeId as string, body);
      return raw.map(adaptNotification);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      if (IS_MOCK || lastPage.length < limit) return undefined;
      const last = lastPage[lastPage.length - 1];
      return last?.cursor ?? last?.occurredAt;
    },
    enabled: (IS_MOCK || (Boolean(activeId) && Boolean(actor))) && enabled,
    refetchInterval: IS_MOCK ? false : COUNT_REFRESH_MS,
  });

  // Склейка страниц в плоский массив.
  const flatData = useMemo(
    () => (IS_MOCK ? mockDisplay : (infinite.data?.pages ?? []).flat()),
    [infinite.data, mockDisplay],
  );

  return {
    ...infinite,
    data: flatData,
    /** Подгрузить следующую страницу (для IntersectionObserver). */
    fetchNextPage: infinite.fetchNextPage,
    hasNextPage: infinite.hasNextPage,
    isFetchingNextPage: infinite.isFetchingNextPage,
  };
};

// ─── Мутации ────────────────────────────────────────────────────────────────

/** Инвалидация всего каскада уведомлений. */
const invalidateNotificationScopes = (
  qc: ReturnType<typeof useQueryClient>,
) => {
  void qc.invalidateQueries({queryKey: notificationKeys.all});
};

/** Пометить одно уведомление прочитанным. */
export const useMarkRead = () => {
  const [, setMockItems] = useAtom(notificationsAtom);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      if (IS_MOCK) {
        setMockItems((prev) =>
          prev.map((n) => (n.id === id ? {...n, unread: false} : n)),
        );
        return;
      }
      await notificationsClient.update(id, {
        readAt: new Date().toISOString(),
      });
    },
    onSuccess: () => {
      invalidateNotificationScopes(qc);
    },
  });
};

/** Пометить все уведомления прочитанными. */
export const useMarkAllRead = () => {
  const [, setMockItems] = useAtom(notificationsAtom);
  const {activeId} = useActiveWorkspace();
  const actor = useNotificationActor();
  const qc = useQueryClient();
  const countKey = notificationKeys.count(activeId ?? 'mock', actor);

  return useMutation({
    mutationFn: async () => {
      if (IS_MOCK) {
        setMockItems((prev) => prev.map((n) => ({...n, unread: false})));
        return;
      }
      return readAllNotifications((before) =>
        notificationsClient.readAll(activeId as string, before),
      );
    },
    onSuccess: (result) => {
      if (result && typeof result.unreadCount === 'number') {
        qc.setQueryData<number>(countKey, result.unreadCount);
      }
    },
    onSettled: () => invalidateNotificationScopes(qc),
  });
};

/** Soft-delete (dismiss) уведомление. */
export const useDismissNotification = () => {
  const [, setMockItems] = useAtom(notificationsAtom);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (IS_MOCK) {
        setMockItems((prev) => prev.filter((n) => n.id !== id));
        return;
      }
      await notificationsClient.remove(id);
    },
    onSuccess: () => invalidateNotificationScopes(qc),
  });
};

// ─── Настройки уведомлений ──────────────────────────────────────────────────

/** Ключи кэша настроек и каналов. */
const settingsKeys = {
  settings: (actor: string | null) =>
    ['notifications', 'settings', actor] as const,
  channels: (actor: string | null) =>
    ['notifications', 'channels', actor] as const,
};

/** Получить настройки уведомлений (сервер создаёт дефолт при первом GET).
 *  `enabled` — доп. гейт (помимо !IS_MOCK), чтобы не слать запрос без необходимости.
 *  staleTime: Infinity — авто-рефетч на enable-переход подавляется; рефетч
 *  инициирует только invalidate из модалки (ровно один запрос за открытие). */
export const useNotificationSettings = (enabled = true) => {
  const actor = useNotificationActor();
  return useQuery({
    queryKey: settingsKeys.settings(actor),
    queryFn: () =>
      IS_MOCK ? getMockSettings() : notificationsClient.getSettings(),
    enabled: Boolean(actor) && enabled,
    staleTime: 0,
  });
};

/** Сохранить patch известных секций; остальные секции сервер не заменяет. */
export const useUpdateSettings = () => {
  const actor = useNotificationActor();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: import('./types').UpdateSettingsDTO) =>
      IS_MOCK
        ? updateMockSettings(body)
        : notificationsClient.updateSettings(body),
    onSuccess: () => {
      // enabled теперь скрывает и bell: перечитываем list/detail/count данного actor.
      void qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === 'notifications' && q.queryKey[2] === actor,
      });
    },
  });
};

// ─── Каналы доставки (Telegram, MAX) ─────────────────────────────────────────

/** Список подключённых каналов.
 *  `polling` — включить refetch каждые 3с (для проверки подключения канала). */
export const useChannels = (polling = false) => {
  const actor = useNotificationActor();
  return useQuery({
    queryKey: settingsKeys.channels(actor),
    queryFn: () => notificationsClient.getChannels(),
    enabled: !IS_MOCK && Boolean(actor),
    staleTime: polling ? 0 : Infinity,
    refetchInterval: polling ? 3000 : false,
  });
};

/** Подключить канал-бот — возвращает код верификации + ссылку на бота. */
export const useInitChannel = () =>
  useMutation({
    mutationFn: (channel: ChannelName) =>
      notificationsClient.initChannel(channel),
  });

/** Отключить канал. */
export const useDisconnectChannel = () => {
  const actor = useNotificationActor();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (connectionSid: string) =>
      notificationsClient.disconnectChannel(connectionSid),
    onSuccess: () => {
      void qc.invalidateQueries({queryKey: settingsKeys.channels(actor)});
    },
  });
};

/**
 * Polling статуса подключения канала после init.
 * Опрашивает channels каждые 3с, пока не увидит `connected`.
 */
export const useChannelConnectionStatus = (
  channel: ChannelName,
  enabled: boolean,
) => {
  const actor = useNotificationActor();
  return useQuery({
    queryKey: settingsKeys.channels(actor),
    queryFn: () => notificationsClient.getChannels(),
    enabled: !IS_MOCK && Boolean(actor) && enabled,
    refetchInterval: (query) => {
      const data = query.state.data;
      const conn = data?.find((c) => c.channel === channel);
      if (conn?.connectionState === 'connected') return false;
      return 3000;
    },
  });
};
